/**
 * @classdesc Diese Snaps werden durch eine Reihe von Punkten festgelegt. Es gibt auch die Möglichkeit,
 * (v.a. für "vertikale" Snaps interessant) dass die Winkel mitsnappen
 * Dabei entspricht der 0 Grad Winkel der Senkrechten (Geraden x = const)
 * @class
 * @param {object[]} points                     Array aus Vektoren
 * @property {number}   x
 * @property {number}   y
 * @param {string}      id                      Id des snappenden Elements
 * @param {string}      edge                    Kante, für die der Snap gilt
 * @param {string}      partName                Part, auf dem der Snap liegt
 * @param {string}      category                "vertical", "horizontal" oder "multiLine"
 * @param {string}      name                    Name des Snaps. Für Partsnaps interessant, z.B. "Brustlinie"
 * @param {number}      [parentTextureSizeY]    dieser Parameter wird erwartet, wenn die category "multiLine" ist.
 */
import {GlobalUtilityPainter} from "./globalUtilityPainter";
import {MathUtils} from "./mathUtils";
import {CONSTANTS} from "./meta_data";
import {THREE} from "./resource/BVH.js";
import {WidthHeight, XY} from "./delme_globals";

export class Snap {
    points;
    forEdge;
    name;
    partName;
    snappedBy;
    category;
    matrices;

    constructor(points:XY[], id, edge, partName, category, name) {

        this.points = points;

        this.forEdge = edge;

        /**Name der Snaplinie, bspw. "Brustlinie"
         * @member {string}
         */
        this.name = name;
        if (typeof (name) === "undefined") {
            this.name = "";
        }

        this.partName = partName;

        /**
         * In diesem Array werden die IDs der Objekte gespeichert, die auf diesen Snap angedockt sind
         * @member {string[]}
         */
        this.snappedBy = [id];

        /**
         * Die Art des Snaps (horizontal, vertical oder multiLine)
         * @member {string[]}
         */
        this.category = category;

        /**
         * Diese Variable wird nur gesetzt, wenn der Snap "multiline" ist
         * @member {null|THREE.Matrix3[]}
         */
        this.matrices = null;

        if (category === "multiLine") {
            this.createMatrices();
        }

    }
    /**
     * erzeugt Matrizen des Snaps. Muss aufgerufen werden, wenn ein multiLine Snap erzeugt wird!
     */
    createMatrices() {
        let i, lenI;
        this.matrices = [];
        for (i = 1, lenI = this.points.length; i < lenI; i++) {
            this.matrices.push(this.createBackwardMatrixForPoints(i - 1, i));
        }
    }

    getMatrixForPoints(a, b) {
        return this.matrices[Math.min(a, b)];
    }

    snaps(partName, translationCoordinate, widthHeight:WidthHeight, snapDistance) {

        let dist, i, result:{point, pointA, pointB, distance}|null = null;
        const anzPoints = this.points.length;

        if (this.partName !== partName) {
            return null;
        }
        const edgeCoordinate = GlobalUtilityPainter.centerCoordinateToEdge(translationCoordinate, widthHeight, this.forEdge);

        for (i = 1; i < anzPoints; i++) {
            dist = MathUtils.distToSegment(edgeCoordinate, this.points[i - 1], this.points[i]);
            if (dist.distance < snapDistance) {
                if (result === null) { //falls noch kein Ergebnis eingetragen ist
                    result = dist as {point, pointA, pointB, distance};
                    result.pointA = this.points[i - 1];
                    result.pointB = this.points[i];
                } else {
                    if (result.distance > dist.distance) { //falls ein näheres Ergebnis vorliegt
                        result = dist as {point, pointA, pointB, distance};
                        result.pointA = this.points[i - 1];
                        result.pointB = this.points[i];
                    }
                }

            }
        }
        if (result !== null) {
            (result as {point, pointA, pointB, distance}).point = GlobalUtilityPainter.edgeCoordinateToCenter((result  as {point, pointA, pointB, distance}).point, widthHeight, this.forEdge);
        }
        return result;
    }

    /**
     * Ein Snap ist dann nicht Self Snap, wenn sein snappedBy Array nicht nur aus der id und den cloneIds besteht, aber die id enthält
     * @param {PartSelectableObject}    partSelOb
     * @return {boolean}       true <==> this.snappedBy besteht nicht nur aus id + cloneIds
     */
    isSelfSnap(partSelOb) {
        let i, len;
        for (i = 0, len = this.snappedBy.length; i < len; i++) {
            if (partSelOb.cloneIds.indexOf(this.snappedBy[i]) === -1 && this.snappedBy[i] !== partSelOb.id) {
                return false;
            }
        }
        return true;

    }

    /**
     * erzeugt die Matrix für die Punkte Nummer indexA und indexB.
     * @param {number}  indexA              index des ersten Punkts
     * @param {number}  indexB              index des zweiten Punkts
     *
     * @return {THREE.Matrix3}
     *
     */
    createBackwardMatrixForPoints(indexA, indexB) {

        const pointA = this.points[indexA],
            pointB = this.points[indexB],
            translation = {x: NaN, y: NaN},
            backwardMatrix = new THREE.Matrix3();

        let rotationBogenmass;
        const lengthX = (pointA.x - pointB.x);
        const lengthY = (pointA.y - pointB.y);
        const scalingX = 1 / CONSTANTS.HELPER_LINEWIDTH;
        const scalingY = 2 / (Math.sqrt(lengthX * lengthX + lengthY * lengthY));

        if (pointA.y === pointB.y) {
            rotationBogenmass = Math.PI / 2; //90 Grad
        } else {
            rotationBogenmass = Math.atan((pointB.x - pointA.x) / (pointB.y - pointA.y));

        }
        translation.x = 0.5 * (pointA.x + pointB.x);
        translation.y = 0.5 * (pointA.y + pointB.y);

        const cosRot = Math.cos(rotationBogenmass);
        const sinRot = Math.sin(rotationBogenmass);

        backwardMatrix.set(
            scalingX * cosRot, //WICHTIG!!! Zuerst skalieren, dann drehen!!!
            scalingX * (-sinRot),
            -translation.x * scalingX * cosRot + (translation.y) * scalingX * sinRot,
            scalingY * sinRot,
            scalingY * cosRot,
            -translation.x * scalingY * sinRot - (translation.y) * scalingY * cosRot,
            0, 0, 1
        );

        return backwardMatrix;
    }

}


